import { useEffect, useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { useQuery } from '@tanstack/react-query';
import { ChatPersonSchema, type ChatPersonDto } from '@taqon/contracts';
import { z } from 'zod';
import { getData } from '../../shared/api/client';
import { PersonName } from '../../shared/ui/person';
import { CHAT, CHAT_KEY } from './chat-api';

const DirectorySchema = z.array(ChatPersonSchema);
const DEBOUNCE_MS = 250;

/**
 * Chooses an active user from the chat directory (SDD §29.3): names only, never an e-mail
 * address. Searches as the user types; `exclude` removes people who cannot be chosen (the caller,
 * existing members).
 */
export function PersonPicker({
  label,
  value,
  onChange,
  exclude = [],
  autoFocus = false,
}: {
  label: string;
  value: ChatPersonDto | null;
  onChange: (person: ChatPersonDto | null) => void;
  exclude?: readonly string[];
  autoFocus?: boolean;
}) {
  const [input, setInput] = useState('');
  const [term, setTerm] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => {
      setTerm(input.trim());
    }, DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [input]);

  const directory = useQuery({
    queryKey: [...CHAT_KEY, 'directory', term],
    enabled: term.length > 0,
    queryFn: () => getData(`${CHAT}/directory?q=${encodeURIComponent(term)}`, DirectorySchema),
  });
  const options = (directory.data ?? []).filter((person) => !exclude.includes(person.userId));

  return (
    <Autocomplete
      options={options}
      value={value}
      onChange={(_, person) => {
        onChange(person);
      }}
      inputValue={input}
      onInputChange={(_, text) => {
        setInput(text);
      }}
      getOptionLabel={(person) => person.displayName}
      isOptionEqualToValue={(a, b) => a.userId === b.userId}
      renderOption={({ key, ...props }, person) => (
        <li key={key} {...props}>
          <PersonName name={person.displayName} userId={person.userId} size={20} />
        </li>
      )}
      filterOptions={(all) => all}
      loading={directory.isFetching}
      noOptionsText={term.length === 0 ? 'Type a name to search' : 'No one found'}
      renderInput={(params) => <TextField {...params} label={label} autoFocus={autoFocus} />}
    />
  );
}
